import type {
  LevelerConfigDto,
  LevelerConfigUpdateDto,
  LevelerMeasureDto,
  LevelerMeasureResultDto,
} from '@camper-diary/shared';
import { api } from './client';

/**
 * La copia locale non è una cache per andare più veloce: è l'unico modo per
 * aprire la livella in una piazzola senza campo. Il calcolo è tutto client-side,
 * quindi con l'ultima configurazione nota la pagina resta pienamente utilizzabile
 * anche con il backend irraggiungibile.
 */
const CACHE_KEY = 'camper-diary:leveler-config';

export type LevelerConfig = LevelerConfigDto;

function readCache(): LevelerConfig | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as LevelerConfig) : null;
  } catch {
    return null;
  }
}

function writeCache(config: LevelerConfig) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(config));
  } catch {
    /* quota piena o storage negato: si continua senza copia locale */
  }
}

export type LoadedConfig = {
  config: LevelerConfig;
  /** true = viene dalla copia locale, la rete non ha risposto */
  offline: boolean;
};

/**
 * Configurazione completa. Se la rete manca si torna all'ultima copia salvata,
 * segnalando che è tale: l'utente deve poter capire se sta guardando dati vecchi.
 */
export async function loadLevelerConfig(): Promise<LoadedConfig> {
  try {
    const config = await api<LevelerConfig>('/leveler');
    writeCache(config);
    return { config, offline: false };
  } catch (err) {
    const cached = readCache();
    if (cached) return { config: cached, offline: true };
    throw err;
  }
}

export async function updateLevelerConfig(
  input: LevelerConfigUpdateDto,
): Promise<LevelerConfig> {
  const config = await api<LevelerConfig>('/leveler', { method: 'PATCH', body: input });
  writeCache(config);
  return config;
}

export function submitLevelerMeasure(measure: LevelerMeasureDto) {
  return api<LevelerMeasureResultDto>('/leveler/measure', {
    method: 'POST',
    body: measure,
  });
}

export async function resetLevelerTuning(): Promise<LevelerConfig> {
  const config = await api<LevelerConfig>('/leveler/tuning', { method: 'DELETE' });
  writeCache(config);
  return config;
}

export const getCachedLevelerConfig = readCache;
